import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { SEO } from "@/components/seo/SEO";

export default function TermsOfUse() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#FAFAF7]">
      <SEO
        title="Terms of Use – Manifest"
        description="Terms of use for Manifest, the AI-powered home inventory app."
        canonical="https://manifestme.me/terms"
      />
      <div className="mx-auto max-w-2xl px-5 py-10 space-y-6 animate-fade-in">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="min-h-[44px] min-w-[44px]">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="font-editorial text-2xl font-bold text-[#111]">Terms of Use</h1>
        </div>

        <div className="rounded-xl border border-[#E5E5E0] bg-white p-6 shadow-sm space-y-6 text-sm text-[#111] leading-relaxed">
          <p className="text-xs text-[#9CA3AF]">Last updated: March 8, 2026</p>

          <section className="space-y-2">
            <h2 className="font-semibold text-base">1. Acceptance of Terms</h2>
            <p className="text-[#6B7280]">
              By accessing or using Manifest ("the Service"), you agree to be bound by these Terms of Use. If you do not agree, you may not use the Service. We reserve the right to update these terms at any time, and continued use constitutes acceptance of any changes.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="font-semibold text-base">2. Description of Service</h2>
            <p className="text-[#6B7280]">
              Manifest is a personal asset management platform that helps you catalog, track, and organize your belongings including purchase records, warranties, and related documents. The Service may include features such as receipt importing via email, AI-assisted data extraction, and document storage.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="font-semibold text-base">3. Account Registration</h2>
            <p className="text-[#6B7280]">
              You must create an account to use the Service. You are responsible for maintaining the confidentiality of your credentials and for all activity that occurs under your account. You agree to provide accurate and current information during registration.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="font-semibold text-base">4. User Content & Data</h2>
            <p className="text-[#6B7280]">
              You retain ownership of all content you upload to Manifest, including photos, receipts, and documents. By using the Service, you grant us a limited license to process, store, and display your content solely for the purpose of providing the Service to you. We do not sell or share your personal data with third parties for marketing purposes.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="font-semibold text-base">5. Acceptable Use</h2>
            <p className="text-[#6B7280]">
              You agree not to misuse the Service. This includes but is not limited to: attempting to gain unauthorized access, interfering with other users' accounts, uploading malicious content, or using the Service for any unlawful purpose. We reserve the right to suspend or terminate accounts that violate these terms.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="font-semibold text-base">6. AI-Assisted Features</h2>
            <p className="text-[#6B7280]">
              Certain features use artificial intelligence to extract information from receipts, documents, and images. While we strive for accuracy, AI-generated data may contain errors. You are responsible for reviewing and verifying all extracted information before relying on it for insurance claims, warranty disputes, or other purposes.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="font-semibold text-base">7. Privacy</h2>
            <p className="text-[#6B7280]">
              Your privacy is important to us. Please review our{" "}
              <a href="/privacy" className="text-[#0F766E] underline hover:text-[#065F46]">Privacy Policy</a>{" "}
              for details on how we collect, use, and protect your information. Data is stored securely and encrypted in transit. You may delete your account and all associated data at any time from the Settings page.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="font-semibold text-base">8. Subscriptions & Payments</h2>
            <p className="text-[#6B7280]">
              Manifest offers a 7-day free trial and paid subscription plans starting at $1.99/month. Payments are processed securely through Stripe. Subscriptions renew automatically unless cancelled. You can manage or cancel your subscription from the Settings page.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="font-semibold text-base">9. Disclaimers</h2>
            <p className="text-[#6B7280]">
              The Service is provided "as is" without warranties of any kind, either express or implied. We do not guarantee that the Service will be uninterrupted, error-free, or that data will never be lost. We recommend maintaining independent backups of important documents.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="font-semibold text-base">10. Limitation of Liability</h2>
            <p className="text-[#6B7280]">
              To the maximum extent permitted by law, Manifest and its operators shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of the Service, including but not limited to loss of data, loss of profits, or damages resulting from reliance on AI-extracted information.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="font-semibold text-base">11. Termination</h2>
            <p className="text-[#6B7280]">
              You may stop using the Service at any time by deleting your account. We may also terminate or suspend your access if you violate these terms. Upon termination, your data will be permanently deleted in accordance with our data retention policies.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="font-semibold text-base">12. Contact</h2>
            <p className="text-[#6B7280]">
              If you have questions about these Terms of Use, contact us at{" "}
              <a href="mailto:privacy@manifestme.me" className="text-[#0F766E] underline hover:text-[#065F46]">
                privacy@manifestme.me
              </a>.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
